// Mastery-skill → multi-LLM playbook generator.
//
// The 100-step AI Mastery Path used to fall through to a single-step
// "frame the challenge" prompt whenever the user clicked a skill that
// didn't keyword-match one of the curated PLAYBOOKS. That meant most
// of the path produced ONE prompt — not a real lesson. This file fixes
// that by generating a tier-aware, multi-step, multi-LLM playbook for
// EVERY mastery skill so the home runner always teaches:
//
//   1. Learn it (cheap model explains it in plain language).
//   2. Try it (mid-tier model walks the user through doing it).
//   3. Compare / level-up (Pro model adds nuance, critique, or scale).
//
// Step depth scales with tier:
//   • Awareness  → 2 steps (learn + try, simple language)
//   • Application→ 3 steps (learn + apply + polish)
//   • Leverage   → 3 steps (apply + chain + measure)
//   • Systems    → 4 steps (design + draft + test + harden)
//   • Architecture→4 steps (frame + architect + tradeoffs + roadmap)
//
// Every step explicitly recommends WHICH model to use for that task and
// WHY — that's the multi-LLM teaching the user asked for. Steps stay in
// plain English and are executable inline (no external tools required)
// so the runner can save the resulting transcript to the user's library.
//
// This is the seed for the future 1000-objective copy-paste library:
// each generated playbook has a stable slug derived from the skill's
// canonical number (`mastery-skill-42`) so saved entries stay valid even
// if a label is later edited.

import type { Playbook, PlaybookStep } from "./playbooks";
import type { MasterySkill, MasteryTier } from "./aiMasteryPath";

// Model shortcuts — mirror the ones in src/data/playbooks.ts so we
// don't sprinkle string literals through this file.
const FLASH_LITE = {
  id: "google/gemini-2.5-flash-lite",
  label: "Gemini Flash Lite",
};
const FLASH = {
  id: "google/gemini-3-flash-preview",
  label: "Gemini 3 Flash",
};
const PRO = {
  id: "google/gemini-2.5-pro",
  label: "Gemini 2.5 Pro",
};
const GPT5_MINI = {
  id: "openai/gpt-5-mini",
  label: "GPT-5 Mini",
};
const GPT5 = {
  id: "openai/gpt-5",
  label: "GPT-5",
};

// Tier → emoji + tagline used on the generated playbook card. Keeps
// the runner header readable without forcing the caller to author copy.
const TIER_META: Record<MasteryTier, { emoji: string; tagline: string }> = {
  Awareness: {
    emoji: "🌱",
    tagline: "Learn it in plain English, then try it once.",
  },
  Application: {
    emoji: "⚡",
    tagline: "Use the right model at each step to actually ship something.",
  },
  Leverage: {
    emoji: "🪜",
    tagline: "Chain models together to multiply your output.",
  },
  Systems: {
    emoji: "🧩",
    tagline: "Design a small system — then test and harden it.",
  },
  Architecture: {
    emoji: "🏛️",
    tagline: "Architect the path: tradeoffs, scale, and a 90-day plan.",
  },
};

function buildSteps(tier: MasteryTier, skill: string): PlaybookStep[] {
  // {topic} is preserved so the runner's existing template substitution
  // keeps working — when the user types a free-form query before clicking
  // the skill, that query becomes the topic; otherwise the runner falls
  // back to the skill label itself.
  const topic = "{topic}";

  switch (tier) {
    case "Awareness":
      return [
        {
          title: `Learn it: "${skill}"`,
          prompt:
            `Teach me "${skill}" in plain English. Assume I'm new to AI.\n\n` +
            `Cover: (1) what it actually means in 2 short paragraphs, ` +
            `(2) one real-world example I'd recognize, (3) the single ` +
            `most common mistake beginners make.\n\n` +
            `My specific angle (optional): ${topic}`,
          model: FLASH_LITE.id,
          modelLabel: FLASH_LITE.label,
          reason:
            "Cheapest tier — perfect for plain-language explanations of foundational concepts.",
        },
        {
          title: "Try it once, with coaching",
          prompt:
            `Now coach me through actually doing "${skill}" right now, ` +
            `inside this chat. Give me a 3-step mini-exercise I can ` +
            `complete in under 2 minutes. After I reply with my attempt, ` +
            `tell me what I got right and the ONE thing I should improve next time.`,
          model: FLASH.id,
          modelLabel: FLASH.label,
          reason:
            "Flash is fast and good at interactive coaching loops — ideal for first-try practice.",
          compareWith: { model: GPT5_MINI.id, modelLabel: GPT5_MINI.label },
        },
      ];

    case "Application":
      return [
        {
          title: `Frame the use case: "${skill}"`,
          prompt:
            `I want to apply "${skill}" to my own work.\n\n` +
            `My context (or topic to apply it to): ${topic}\n\n` +
            `Ask me up to 3 short questions to lock down the use case, ` +
            `then summarize it back as a one-line goal I can use as the ` +
            `brief for the next step.`,
          model: GPT5_MINI.id,
          modelLabel: GPT5_MINI.label,
          reason:
            "Mini is great at structured discovery questions without overspending.",
        },
        {
          title: "Draft a first version",
          prompt:
            `Using the brief from the previous step, produce a complete ` +
            `first draft of "${skill}" applied to my context. Keep it ` +
            `practical and copy-pasteable. End with a 3-bullet checklist ` +
            `of what to review before shipping.`,
          model: FLASH.id,
          modelLabel: FLASH.label,
          reason:
            "Flash is fast + cheap for first drafts — we'll polish with a stronger model next.",
        },
        {
          title: "Polish + sanity-check",
          prompt:
            `Critique the draft above. Score it 1–10 on (a) clarity, ` +
            `(b) usefulness, (c) effort-to-result. Rewrite the weakest ` +
            `section and explain in one sentence why your rewrite is better.`,
          model: PRO.id,
          modelLabel: PRO.label,
          reason:
            "Pro reasoning catches the trade-offs and weak spots a Flash draft misses.",
        },
      ];

    case "Leverage":
      return [
        {
          title: `Map where "${skill}" multiplies you`,
          prompt:
            `I want to use "${skill}" to multiply my output.\n\n` +
            `My context: ${topic}\n\n` +
            `Identify the top 3 places in my workflow where this skill ` +
            `would save the most hours per week. For each: estimate ` +
            `hours saved, the model I should use, and the trigger that ` +
            `tells me to apply it.`,
          model: PRO.id,
          modelLabel: PRO.label,
          reason:
            "Pro reasoning is best at workflow analysis and ROI estimation.",
        },
        {
          title: "Chain it: build a 3-step recipe",
          prompt:
            `Pick the highest-leverage spot from above. Design a 3-step ` +
            `multi-model recipe that automates it. For each step list: ` +
            `the prompt, the recommended model, the cost tier (cheap / ` +
            `mid / pro), and what gets passed to the next step.`,
          model: GPT5_MINI.id,
          modelLabel: GPT5_MINI.label,
          reason:
            "Mini reliably produces structured multi-step recipes without burning Pro credits.",
        },
        {
          title: "Measure it",
          prompt:
            `Define how I'll know the recipe is working in week 1. ` +
            `Give me: 2 leading metrics, 1 lagging metric, a quick weekly ` +
            `review prompt I can paste into a chat to audit my own usage, ` +
            `and the single fail-safe sign I should kill the workflow.`,
          model: GPT5.id,
          modelLabel: GPT5.label,
          reason:
            "GPT-5 is best at honest self-evaluation frameworks and red-flag detection.",
        },
      ];

    case "Systems":
      return [
        {
          title: `Architect the system: "${skill}"`,
          prompt:
            `I want to build a real system around "${skill}" — not a ` +
            `one-off prompt.\n\n` +
            `My context / what it should do: ${topic}\n\n` +
            `Sketch the system as: inputs, processing steps (with which ` +
            `model handles each), storage/state, outputs, and the human ` +
            `touch-points. Use plain English — no code yet.`,
          model: PRO.id,
          modelLabel: PRO.label,
          reason:
            "Pro reasoning is essential for system design — it catches missing edges Flash skips.",
        },
        {
          title: "Draft the core prompts + model picks",
          prompt:
            `For each processing step in the architecture above, write ` +
            `the actual prompt I'd run AND name the recommended model ` +
            `with one-sentence justification. Group by step. Keep prompts ` +
            `under 6 lines each.`,
          model: GPT5_MINI.id,
          modelLabel: GPT5_MINI.label,
          reason:
            "Mini drafts tight, structured prompt sets cheaply and consistently.",
          compareWith: { model: PRO.id, modelLabel: PRO.label },
        },
        {
          title: "Stress-test it",
          prompt:
            `Be a skeptical reviewer. List the 5 most likely failure ` +
            `modes of this system (bad input, hallucination, cost spike, ` +
            `latency, drift). For each: how to detect, how to mitigate, ` +
            `which model to use for the mitigation.`,
          model: GPT5.id,
          modelLabel: GPT5.label,
          reason:
            "GPT-5 is best at adversarial what-if reasoning across complex systems.",
        },
        {
          title: "Ship plan + handoff doc",
          prompt:
            `Produce a 1-page handoff doc: system name, what it does, ` +
            `the 3-step ship plan for week 1, the success metric, and ` +
            `a copy-paste runbook anyone on my team could follow. End ` +
            `with the next 2 features to add once v1 is live.`,
          model: FLASH.id,
          modelLabel: FLASH.label,
          reason:
            "Flash is plenty for tight structured handoff docs — saves the heavier models for design + review.",
        },
      ];

    case "Architecture":
      return [
        {
          title: `Frame the bet: "${skill}"`,
          prompt:
            `I'm thinking architecturally about "${skill}".\n\n` +
            `My context / what I'm trying to build or change: ${topic}\n\n` +
            `In 1 paragraph, restate the core bet I'm making. In a second ` +
            `paragraph, name the 3 strongest second-order effects (good and ` +
            `bad) if this works. Be honest — flag where I'm hand-waving.`,
          model: GPT5.id,
          modelLabel: GPT5.label,
          reason:
            "GPT-5 is best at honest, high-level strategic framing and second-order thinking.",
        },
        {
          title: "Architect 3 viable paths",
          prompt:
            `Design 3 distinct architectures that could deliver this bet. ` +
            `For each path: a 1-line summary, the model stack (which LLMs ` +
            `do what), the data/state requirements, the team shape, and ` +
            `the speed-to-market estimate.`,
          model: PRO.id,
          modelLabel: PRO.label,
          reason:
            "Pro reasoning compares architectural tradeoffs better than any cheaper model.",
          compareWith: { model: GPT5.id, modelLabel: GPT5.label },
        },
        {
          title: "Pick + commit",
          prompt:
            `Pick the strongest path. Justify in 4 bullets: why now, ` +
            `why us, why not the others, and the single biggest risk. ` +
            `End with the smallest possible v0 that would prove the ` +
            `architecture works inside 14 days.`,
          model: PRO.id,
          modelLabel: PRO.label,
          reason:
            "Pro is the right call for the commit-step — the cost of being wrong here is high.",
        },
        {
          title: "90-day rollout",
          prompt:
            `Turn the v0 into a 90-day rollout: month 1 = prove, month 2 ` +
            `= scale, month 3 = harden. For each month list: the goal, ` +
            `the 3 deliverables, the model spend tier, and the kill-switch ` +
            `metric that tells me to pivot.`,
          model: GPT5_MINI.id,
          modelLabel: GPT5_MINI.label,
          reason:
            "Mini reliably produces structured rollout plans without burning Pro credits on logistics.",
        },
      ];
  }
}

/**
 * Generate a full multi-LLM playbook for any mastery skill. The slug is
 * deterministic (`mastery-skill-<num>`) so saved-library entries stay
 * stable across label edits, and so we can grow toward the 1000-objective
 * copy-paste library without breaking older bookmarks.
 */
export function playbookForMasterySkill(skill: MasterySkill): Playbook {
  const meta = TIER_META[skill.tier];
  return {
    slug: `mastery-skill-${skill.num}`,
    title: skill.label,
    tagline: `${skill.tier} · ${meta.tagline}`,
    emoji: meta.emoji,
    steps: buildSteps(skill.tier, skill.label),
  };
}

/**
 * Lookup helper for the home picker: given a free-form query, see if it
 * matches a known mastery skill label exactly (case-insensitive). When
 * it does, return the generated playbook so a click on a rotator chip
 * always opens the rich multi-step lesson — not the one-step fallback.
 */
export function findMasterySkillPlaybook(
  query: string,
  pool: MasterySkill[],
): Playbook | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;
  const exact = pool.find((s) => s.label.toLowerCase() === needle);
  if (exact) return playbookForMasterySkill(exact);
  // Partial: skill label contained in query OR query contained in label.
  const partial = pool.find((s) => {
    const l = s.label.toLowerCase();
    return l.includes(needle) || needle.includes(l);
  });
  return partial ? playbookForMasterySkill(partial) : null;
}